// D1 API implementation - connects to Platform PM backend
// Maps D1 API responses to the expected bh_* format used by the SPA

const API_BASE = import.meta.env.VITE_API_URL || 'https://platform-pm.bryan-hope.workers.dev';

// Helper for making requests
async function fetchD1(
  endpoint: string,
  options: RequestInit = {}
): Promise<any> {
  const url = `${API_BASE}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('D1 API error:', response.status, errorText);
    throw new Error(`D1 API error: ${response.status} - ${errorText}`);
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}

// Priority mapping
const priorityToNumber = (priority: string): number => {
  switch (priority?.toLowerCase()) {
    case 'urgent': return 4;
    case 'high': return 3;
    case 'medium': return 2;
    case 'low': return 1;
    default: return 0;
  }
};

const numberToPriority = (num: number): string => {
  switch (num) {
    case 4: return 'urgent';
    case 3: return 'high';
    case 2: return 'medium';
    case 1: return 'low';
    default: return 'none';
  }
};

// Transform D1 responses to expected format
const transformTask = (task: any) => ({
  bh_workitemid: task.id,
  bh_name: task.title,
  bh_description: task.description,
  bh_identifier: task.id?.split('_')[1] || task.id,
  bh_sequenceid: task.position || 0,
  bh_priority: priorityToNumber(task.priority),
  bh_state: task.statusId,
  _bh_state_value: task.statusId,
  bh_project: task.projectId,
  bh_assignee: task.assigneeId,
  bh_cycle: null, // D1 doesn't have cycles yet
  bh_module: null, // D1 doesn't have modules yet
  bh_epic: null, // D1 doesn't have epics yet
  bh_parent: task.parentId,
  bh_estimate: task.estimateHours,
  bh_startdate: task.startDate,
  bh_targetdate: task.dueDate,
  createdon: task.createdAt,
});

const transformProject = (project: any) => ({
  bh_projectid: project.id,
  bh_name: project.name,
  bh_identifier: project.slug?.toUpperCase() || 'PRJ',
  bh_description: project.description,
  bh_icon: 'folder',
  bh_workspace: project.workspaceId,
  createdon: project.createdAt,
});

const transformStatus = (status: any) => ({
  bh_stateid: status.id,
  bh_name: status.name,
  bh_color: status.color,
  bh_group: status.isDone ? 4 : status.position <= 1 ? 1 : status.position <= 2 ? 2 : 3,
  bh_sequence: status.position,
  bh_order: status.position,
  bh_project: status.workspaceId, // In D1, statuses are workspace-level
});

const transformLabel = (label: any) => ({
  bh_labelid: label.id,
  bh_name: label.name,
  bh_color: label.color,
  bh_project: label.workspaceId,
});

const transformComment = (comment: any) => ({
  bh_commentid: comment.id,
  bh_content: comment.content,
  bh_workitem: comment.taskId,
  bh_author: comment.userId,
  createdon: comment.createdAt,
});

// Store workspace ID globally (D1 auto-creates a default workspace)
let cachedWorkspaceId: string | null = null;

async function getWorkspaceId(): Promise<string> {
  if (cachedWorkspaceId) return cachedWorkspaceId;

  // D1 creates a default workspace on first request
  // We'll get it from the statuses endpoint
  const response = await fetchD1('/api/statuses');
  if (response.statuses?.length > 0) {
    const wsId = response.statuses[0].workspaceId as string;
    cachedWorkspaceId = wsId;
    return wsId;
  }

  // Fallback - trigger workspace creation by calling any endpoint
  await fetchD1('/api/projects');
  const statusResponse = await fetchD1('/api/statuses');
  const wsId = (statusResponse.statuses?.[0]?.workspaceId as string) || 'default';
  cachedWorkspaceId = wsId;
  return wsId;
}

// D1 API implementation
export const d1Api = {
  // Workspaces - D1 has a single default workspace
  getWorkspaces: async () => {
    const workspaceId = await getWorkspaceId();
    return [{
      bh_workspaceid: workspaceId,
      bh_name: 'Default Workspace',
      bh_description: 'Main workspace',
      createdon: new Date().toISOString(),
    }];
  },

  getWorkspace: async (id: string) => {
    return {
      bh_workspaceid: id,
      bh_name: 'Default Workspace',
      bh_description: 'Main workspace',
      createdon: new Date().toISOString(),
    };
  },

  createWorkspace: async (data: any) => {
    // D1 doesn't support multiple workspaces yet, return mock
    const workspaceId = await getWorkspaceId();
    return {
      bh_workspaceid: workspaceId,
      bh_name: data.bh_name,
      bh_description: data.bh_description || '',
      createdon: new Date().toISOString(),
    };
  },

  updateWorkspace: async (id: string, data: any) => {
    return { bh_workspaceid: id, ...data };
  },

  deleteWorkspace: async (_id: string) => {
    // No-op for D1
  },

  // Projects
  getProjects: async (_workspaceId: string) => {
    const response = await fetchD1('/api/projects');
    return (response.projects || []).map(transformProject);
  },

  getProject: async (id: string) => {
    const response = await fetchD1(`/api/projects/${id}`);
    return response.project ? transformProject(response.project) : null;
  },

  createProject: async (data: any) => {
    const response = await fetchD1('/api/projects', {
      method: 'POST',
      body: JSON.stringify({
        name: data.bh_name,
        slug: data.bh_identifier?.toLowerCase() || data.bh_name.toLowerCase().replace(/\s+/g, '-'),
        description: data.bh_description || '',
      }),
    });
    return transformProject(response.project);
  },

  updateProject: async (id: string, data: any) => {
    const body: any = {};
    if (data.bh_name) body.name = data.bh_name;
    if (data.bh_identifier) body.slug = data.bh_identifier.toLowerCase();
    if (data.bh_description !== undefined) body.description = data.bh_description;

    const response = await fetchD1(`/api/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.project ? transformProject(response.project) : { bh_projectid: id, ...data };
  },

  deleteProject: async (id: string) => {
    await fetchD1(`/api/projects/${id}`, { method: 'DELETE' });
  },

  // States/Statuses
  getStates: async (_projectId: string) => {
    const response = await fetchD1('/api/statuses');
    return (response.statuses || []).map(transformStatus);
  },

  createState: async (data: any) => {
    const response = await fetchD1('/api/statuses', {
      method: 'POST',
      body: JSON.stringify({
        name: data.bh_name,
        color: data.bh_color,
        position: data.bh_sequence || data.bh_order || 0,
        isDone: data.bh_group === 4,
      }),
    });
    return transformStatus(response.status);
  },

  updateState: async (id: string, data: any) => {
    const body: any = {};
    if (data.bh_name !== undefined) body.name = data.bh_name;
    if (data.bh_color !== undefined) body.color = data.bh_color;
    if (data.bh_sequence !== undefined || data.bh_order !== undefined) {
      body.position = data.bh_sequence || data.bh_order;
    }
    if (data.bh_group !== undefined) body.isDone = data.bh_group === 4;

    const response = await fetchD1(`/api/statuses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.status ? transformStatus(response.status) : { bh_stateid: id, ...data };
  },

  deleteState: async (id: string) => {
    await fetchD1(`/api/statuses/${id}`, { method: 'DELETE' });
  },

  // Labels
  getLabels: async (_projectId: string) => {
    const response = await fetchD1('/api/labels');
    return (response.labels || []).map(transformLabel);
  },

  createLabel: async (data: any) => {
    const response = await fetchD1('/api/labels', {
      method: 'POST',
      body: JSON.stringify({
        name: data.bh_name,
        color: data.bh_color,
        description: data.bh_description,
      }),
    });
    return transformLabel(response.label);
  },

  updateLabel: async (id: string, data: any) => {
    const response = await fetchD1(`/api/labels/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: data.bh_name,
        color: data.bh_color,
        description: data.bh_description,
      }),
    });
    return response.label ? transformLabel(response.label) : { bh_labelid: id, ...data };
  },

  deleteLabel: async (id: string) => {
    await fetchD1(`/api/labels/${id}`, { method: 'DELETE' });
  },

  // Work Items / Tasks
  getWorkItems: async (projectId: string) => {
    const params = projectId ? `?project=${projectId}` : '';
    const response = await fetchD1(`/api/tasks${params}`);
    return (response.tasks || []).map(transformTask);
  },

  getWorkItem: async (id: string) => {
    const response = await fetchD1(`/api/tasks/${id}`);
    return response.task ? transformTask(response.task) : null;
  },

  createWorkItem: async (data: any) => {
    const response = await fetchD1('/api/tasks', {
      method: 'POST',
      body: JSON.stringify({
        title: data.bh_name,
        description: data.bh_description || '',
        priority: numberToPriority(data.bh_priority || 0),
        statusId: data.bh_state,
        projectId: data.bh_project || null,
        assigneeId: data.bh_assignee || null,
        parentId: data.bh_parent || null,
        estimateHours: data.bh_estimate || null,
        startDate: data.bh_startdate || null,
        dueDate: data.bh_targetdate || null,
      }),
    });
    return transformTask(response.task);
  },

  updateWorkItem: async (id: string, data: any) => {
    const body: any = {};
    if (data.bh_name !== undefined) body.title = data.bh_name;
    if (data.bh_description !== undefined) body.description = data.bh_description;
    if (data.bh_priority !== undefined) body.priority = numberToPriority(data.bh_priority);
    if (data.bh_state !== undefined) body.statusId = data.bh_state;
    if (data.bh_project !== undefined) body.projectId = data.bh_project;
    if (data.bh_assignee !== undefined) body.assigneeId = data.bh_assignee;
    if (data.bh_estimate !== undefined) body.estimateHours = data.bh_estimate;
    if (data.bh_startdate !== undefined) body.startDate = data.bh_startdate;
    if (data.bh_targetdate !== undefined) body.dueDate = data.bh_targetdate;

    const response = await fetchD1(`/api/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.task ? transformTask(response.task) : { bh_workitemid: id, ...data, _bh_state_value: data.bh_state };
  },

  deleteWorkItem: async (id: string) => {
    await fetchD1(`/api/tasks/${id}`, { method: 'DELETE' });
  },

  // Sub-issues
  getSubIssues: async (parentId: string) => {
    const response = await fetchD1(`/api/tasks?parent=${parentId}`);
    return (response.tasks || []).map(transformTask);
  },

  // Bulk operations
  bulkUpdateWorkItems: async (ids: string[], data: any) => {
    const body: any = {};
    if (data.bh_state !== undefined) body.statusId = data.bh_state;
    if (data.bh_priority !== undefined) body.priority = numberToPriority(data.bh_priority);
    if (data.bh_assignee !== undefined) body.assigneeId = data.bh_assignee;
    if (data.bh_project !== undefined) body.projectId = data.bh_project;

    await fetchD1('/api/bulk/update', {
      method: 'POST',
      body: JSON.stringify({ taskIds: ids, updates: body }),
    });

    // Return updated items
    const tasks = await Promise.all(ids.map(id => d1Api.getWorkItem(id)));
    return tasks.filter(Boolean);
  },

  bulkDeleteWorkItems: async (ids: string[]) => {
    await fetchD1('/api/bulk/delete', {
      method: 'POST',
      body: JSON.stringify({ taskIds: ids }),
    });
  },

  // Comments
  getComments: async (workItemId: string) => {
    const response = await fetchD1(`/api/tasks/${workItemId}/comments`);
    return (response.comments || []).map(transformComment);
  },

  createComment: async (data: any) => {
    const response = await fetchD1(`/api/tasks/${data.bh_workitem}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content: data.bh_content }),
    });
    return transformComment(response.comment);
  },

  updateComment: async (id: string, data: any) => {
    const response = await fetchD1(`/api/comments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ content: data.bh_content }),
    });
    return response.comment ? transformComment(response.comment) : { bh_commentid: id, ...data };
  },

  deleteComment: async (id: string) => {
    await fetchD1(`/api/comments/${id}`, { method: 'DELETE' });
  },

  // Cycles - Not yet in D1, return empty
  getCycles: async (_projectId: string) => [],
  getCycle: async (_id: string) => undefined,
  createCycle: async (data: any) => ({ bh_cycleid: `cycle-${Date.now()}`, ...data }),
  updateCycle: async (id: string, data: any) => ({ bh_cycleid: id, ...data }),
  deleteCycle: async (_id: string) => {},

  // Modules - Not yet in D1, return empty
  getModules: async (_projectId: string) => [],
  getModule: async (_id: string) => undefined,
  createModule: async (data: any) => ({ bh_moduleid: `module-${Date.now()}`, ...data }),
  updateModule: async (id: string, data: any) => ({ bh_moduleid: id, ...data }),
  deleteModule: async (_id: string) => {},

  // Epics - Not yet in D1, return empty
  getEpics: async (_projectId: string) => [],
  getEpic: async (_id: string) => undefined,
  createEpic: async (data: any) => ({ bh_epicid: `epic-${Date.now()}`, ...data }),
  updateEpic: async (id: string, data: any) => ({ bh_epicid: id, ...data }),
  deleteEpic: async (_id: string) => {},

  // Members - Return empty for now
  getMembers: async (_workspaceId: string) => [],
  getMember: async (_id: string) => undefined,

  // Files - Not yet implemented
  getFiles: async (_workItemId: string) => [],
  uploadFile: async (_file: File, _workItemId: string) => {
    throw new Error('File upload not yet implemented');
  },
  deleteFile: async (_id: string) => {
    throw new Error('File delete not yet implemented');
  },

  // Search
  search: async (query: string, projectId: string) => {
    const params = new URLSearchParams({ search: query });
    if (projectId) params.append('project', projectId);

    const response = await fetchD1(`/api/tasks?${params}`);
    return {
      workItems: (response.tasks || []).map(transformTask),
      cycles: [],
      modules: [],
      pages: [],
      epics: [],
    };
  },

  // Pages - Not yet in D1, return empty
  getPages: async (_projectId: string) => [],
  getPage: async (_id: string) => undefined,
  createPage: async (data: any) => ({ bh_pageid: `page-${Date.now()}`, ...data, createdon: new Date().toISOString(), modifiedon: new Date().toISOString() }),
  updatePage: async (id: string, data: any) => ({ bh_pageid: id, ...data, modifiedon: new Date().toISOString() }),
  deletePage: async (_id: string) => {},

  // Views - Not yet in D1, return empty
  getViews: async (_projectId: string) => [],
  getView: async (_id: string) => undefined,
  createView: async (data: any) => ({ bh_viewid: `view-${Date.now()}`, ...data }),
  updateView: async (id: string, data: any) => ({ bh_viewid: id, ...data }),
  deleteView: async (_id: string) => {},

  // Activities - Not yet implemented
  getActivities: async (_workItemId: string) => [],
  createActivity: async (data: any) => ({ bh_activityid: `act-${Date.now()}`, ...data }),

  // Notifications - Not yet implemented
  getNotifications: async (_userId: string) => [],
  markNotificationRead: async (_id: string) => {},
  markAllNotificationsRead: async (_userId: string) => {},

  // Relations - Not yet in D1
  getRelations: async (_issueId: string) => [],
  createRelation: async (data: any) => ({ bh_relationid: `rel-${Date.now()}`, ...data }),
  deleteRelation: async (_id: string) => {},
};
